import IconClose from '@mnd-frontend/icons/components/Close';
import IconImgBroken from '@mnd-frontend/icons/components/ImgBroken';
import IconPlus from '@mnd-frontend/icons/components/Plus';
import { withLineClamp } from '@mnd-frontend/ui/publish/components/utils';
import {
  GREY_200,
  GREY_400,
  GREY_600,
  GREY_700,
  GREY_800,
  GREY_1000,
  RADIUS_LG,
  RED_700,
  SIZING_1_5,
  SIZING_6,
  SPACING_1,
  SPACING_1_5,
  SPACING_2,
} from '@mnd-frontend/ui/shared/tokens';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

const LOGO_HEIGHT = '3rem';
const CARD_BORDER_WIDTH = '0.25';
const CONTENT_WIDTH = '7.5';
export const CARD_WIDTH = +CARD_BORDER_WIDTH * 2 + +CONTENT_WIDTH + 'rem';
const TRANSITION = 'all 200ms ease';

const CardContainer = styled.div<{ $hasBg: boolean }>`
  background-color: ${({ $hasBg }) => ($hasBg ? GREY_200 : 'transparent')};
  border-radius: calc(${RADIUS_LG} - ${CARD_BORDER_WIDTH + 'rem'});
  color: ${GREY_1000};
  padding: ${SPACING_2};
  text-align: center;
  width: 7.5rem;
  aspect-ratio: 1 / 1;
  box-sizing: border-box;
  border-width: ${CARD_BORDER_WIDTH + 'rem'};
  overflow: hidden;
`;

const BrandLogoContainer = styled.div`
  min-width: 5.5rem;
  height: ${LOGO_HEIGHT};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  .logo-img {
    object-fit: contain;
    height: auto;
    width: 100%;
    max-width: ${SIZING_6};
  }
`;

const LogoMask = styled.div`
  aspect-ratio: 1 / 1;
  width: calc(${LOGO_HEIGHT} + ${SPACING_1});
  position: absolute;
  top: ${SPACING_1_5};
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(245, 245, 245, 0.9);
`;

const OverlayBase = css`
  aspect-ratio: 1 / 1;
  border-width: ${CARD_BORDER_WIDTH + 'rem'};
  border-style: solid;
  border-radius: ${RADIUS_LG};
  background-color: transparent;
  padding: 0;
`;

const ButtonAdd = styled.button`
  aspect-ratio: 1 / 1;
  width: ${CARD_WIDTH};
  border-width: calc(${CARD_BORDER_WIDTH + 'rem'} / 2);
  border-style: dashed;
  border-radius: ${RADIUS_LG};
  border-color: ${GREY_400};
  background-color: transparent;
  padding: 0;
  cursor: pointer;
  transition: ${TRANSITION};

  .content {
    color: ${GREY_800};
    transition: ${TRANSITION};
  }

  .mnd-icon > svg {
    transition: ${TRANSITION};
    color: ${GREY_600};
  }

  &:disabled {
    border-color: transparent;

    .content {
      color: transparent;
    }

    .mnd-icon > svg {
      color: transparent;
    }
  }
`;

const ButtonDefault = styled.button`
  ${OverlayBase}

  cursor: pointer;
  transition: ${TRANSITION};
  border-color: ${GREY_200};
  filter: grayscale(0);

  &:hover {
    border-color: ${GREY_400};
  }

  &:disabled {
    border-color: ${GREY_200};
    background-color: ${GREY_200};
    filter: grayscale(1);
    cursor: not-allowed;

    .content {
      color: ${GREY_700};
    }

    &:hover {
      border-color: transparent;
    }
  }
`;

const ButtonSelected = styled.button`
  ${OverlayBase}

  border-color: ${RED_700};
  cursor: pointer;
  position: relative;
  display: grid;
  grid-template-rows: 2rem auto;
  grid-template-columns: auto 2rem;

  &::before,
  &::after {
    content: '';
    position: absolute;
    display: block;
    top: 8px;
    z-index: 1;
  }
  &::before {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    right: 8px;
    background-color: ${RED_700};
  }

   &::after {
    width: 4px;
    height: 8px;
    right: 14px;
    top: 11px;
    border-width: 1.5px;
    border-color: white;
    border-style: solid;
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
  }

   .mnd-icon:has(.cross-hover) {
    display: none;
    position: absolute;
    left: 8px;
    top: 8px;
    grid-row: 1 / 2;
    grid-column: 2 / 3;
    z-index: 5;
   }

   .content {
    grid-row: 1 / 3;
    grid-column: 1 / 3;
   }


  &:hover {
    &::after {
      display: none;
    }

    .mnd-icon:has(.cross-hover) {
      display: block;
    }
  }
`;

const CardLabel = styled.div`
  color: inherit;
  font-weight: 600;
  font-size: ${SIZING_1_5};
  margin-top: ${SPACING_1_5};
  height: 2rem;
  line-height: 1rem;
  overflow-wrap: break-word;
  ${withLineClamp(2)}
`;

export const STATUS = {
  DEFAULT: 'default',
  DISABLED: 'disabled',
  SELECTED: 'selected',
  EMPTY: 'empty',
  EMPTY_DISABLED: 'emptyDisabled',
} as const;

type Status = (typeof STATUS)[keyof typeof STATUS];

type CardCompetitorClickableProps = {
  status: Exclude<Status, 'disabled'>;
  logoSrc?: string | null;
  name: string;
  onClick: () => void;
};

type CardCompetitorDisabledProps = {
  status: 'disabled';
  logoSrc?: string | null;
  name: string;
};

type CardCompetitorEmptyProps = {
  status: 'empty';
};

type CardCompetitorEmptyDisabledProps = {
  status: 'emptyDisabled';
};

type CardCompetitorProps =
  | CardCompetitorClickableProps
  | CardCompetitorDisabledProps
  | CardCompetitorEmptyProps
  | CardCompetitorEmptyDisabledProps;

type LabelProps = {
  name: string;
  hasBg: boolean;
  logoSrc?: string | null | undefined;
};

const Label = memo(({ logoSrc, name, hasBg }: LabelProps) => (
  <CardContainer className="content" $hasBg={hasBg} title={name}>
    {logoSrc ? (
      <BrandLogoContainer>
        <img className="logo-img" src={logoSrc} alt="" width="48" height="48" />
      </BrandLogoContainer>
    ) : (
      <IconImgBroken $size="md" />
    )}

    <CardLabel>{name}</CardLabel>
  </CardContainer>
));

const CardCompetitor = ({ status, ...props }: CardCompetitorProps) => {
  const { t } = useTranslation();

  const { onClick } = props as CardCompetitorProps & CardCompetitorClickableProps;

  if (status === STATUS.EMPTY || status === STATUS.EMPTY_DISABLED) {
    return (
      <ButtonAdd
        type="button"
        onClick={onClick}
        aria-label={t('aria_label_onboarding_card_competitor_add')}
        disabled={status === STATUS.EMPTY_DISABLED}
      >
        <CardContainer className="content" $hasBg={false}>
          <BrandLogoContainer>
            <IconPlus $size="md" color={GREY_600} />
          </BrandLogoContainer>
          <CardLabel>{t('common_add')}</CardLabel>
        </CardContainer>
      </ButtonAdd>
    );
  }

  if (status === STATUS.SELECTED) {
    return (
      <ButtonSelected type="button" onClick={onClick} aria-pressed={true}>
        <Label hasBg={false} {...(props as Omit<LabelProps, 'hasBg'>)} />
        <IconClose $size="xxs" className="cross-hover" color="white" />
      </ButtonSelected>
    );
  }

  return (
    <ButtonDefault type="button" onClick={onClick} disabled={status === STATUS.DISABLED}>
      <Label hasBg={false} {...(props as Omit<LabelProps, 'hasBg'>)} />
      {status === STATUS.DISABLED && <LogoMask aria-hidden></LogoMask>}
    </ButtonDefault>
  );
};

export default CardCompetitor;
